import React, { useState, useEffect } from 'react';
import { 
  Building2, Plus, Pencil, Trash2, Star, Check, X, 
  GripVertical, Upload, Loader2, Eye, EyeOff, CalendarClock
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import ImageUploader from '../components/ImageUploader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BanksPage = () => {
  const { token } = useAuth();
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBank, setEditingBank] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    logo_url: '',
    max_months: '',
    min_rate: '',
    benefits: [''],
    is_highlighted: false,
    is_active: true,
    order: 0,
    promo_expiration_date: '',
    has_interest_free_option: false
  });

  useEffect(() => {
    fetchBanks();
  }, []);

  const fetchBanks = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/banks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBanks(response.data);
    } catch (error) {
      console.error('Error fetching banks:', error);
    } finally {
      setLoading(false);
    }
  };

  const seedBanks = async () => {
    try {
      await axios.post(`${API_URL}/api/banks/seed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchBanks();
    } catch (error) {
      console.error('Error seeding banks:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const dataToSend = {
        ...formData,
        benefits: formData.benefits.filter(b => b.trim() !== ''),
        max_months: formData.max_months ? parseInt(formData.max_months) : null,
        promo_expiration_date: formData.promo_expiration_date || null
      };
      
      if (editingBank) {
        await axios.put(`${API_URL}/api/banks/${editingBank.id}`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API_URL}/api/banks`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      setShowModal(false);
      setEditingBank(null);
      resetForm();
      fetchBanks();
    } catch (error) {
      console.error('Error saving bank:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (bankId) => {
    if (!window.confirm('¿Estás seguro de eliminar este banco?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/banks/${bankId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchBanks();
    } catch (error) {
      console.error('Error deleting bank:', error);
    }
  };

  const toggleActive = async (bank) => {
    try {
      await axios.put(`${API_URL}/api/banks/${bank.id}`, 
        { is_active: !bank.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchBanks();
    } catch (error) {
      console.error('Error toggling bank:', error);
    }
  };

  const toggleHighlight = async (bank) => {
    try {
      await axios.put(`${API_URL}/api/banks/${bank.id}`, 
        { is_highlighted: !bank.is_highlighted },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchBanks();
    } catch (error) {
      console.error('Error toggling highlight:', error);
    }
  };

  const openEditModal = (bank) => {
    setEditingBank(bank);
    setFormData({
      name: bank.name,
      logo_url: bank.logo_url || '',
      max_months: bank.max_months || '',
      min_rate: bank.min_rate || '',
      benefits: bank.benefits && bank.benefits.length > 0 ? bank.benefits : [''],
      is_highlighted: bank.is_highlighted,
      is_active: bank.is_active,
      order: bank.order,
      promo_expiration_date: bank.promo_expiration_date || '',
      has_interest_free_option: bank.has_interest_free_option || false
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      logo_url: '',
      max_months: '',
      min_rate: '',
      benefits: [''],
      is_highlighted: false,
      is_active: true,
      order: banks.length + 1,
      promo_expiration_date: '',
      has_interest_free_option: false
    });
  };

  const addBenefit = () => {
    setFormData({ ...formData, benefits: [...formData.benefits, ''] });
  };

  const removeBenefit = (index) => {
    const newBenefits = formData.benefits.filter((_, i) => i !== index);
    setFormData({ ...formData, benefits: newBenefits.length > 0 ? newBenefits : [''] });
  };

  const updateBenefit = (index, value) => {
    const newBenefits = [...formData.benefits];
    newBenefits[index] = value;
    setFormData({ ...formData, benefits: newBenefits });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-PY', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return null;
    }
  };

  const isExpired = (dateStr) => {
    if (!dateStr) return false;
    try {
      return new Date(dateStr) < new Date();
    } catch {
      return false;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="banks-admin-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">Bancos Aliados</h1>
          <p className="text-zinc-500">Gestiona los bancos que aparecen en la página de financiamiento</p>
        </div>
        <div className="flex gap-3">
          {banks.length === 0 && (
            <button
              onClick={seedBanks}
              className="px-4 py-2 bg-zinc-100 text-zinc-700 rounded-lg hover:bg-zinc-200 transition-colors"
            >
              Cargar bancos por defecto
            </button>
          )}
          <button
            onClick={() => {
              resetForm();
              setEditingBank(null);
              setShowModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Nuevo Banco
          </button>
        </div>
      </div>

      {/* Banks List */}
      {banks.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-12 text-center">
          <Building2 className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-zinc-900 mb-2">No hay bancos todavía</h3>
          <p className="text-zinc-500">Carga los bancos por defecto o agrega uno nuevo</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {banks.map((bank) => (
            <div 
              key={bank.id}
              className={`bg-white rounded-xl shadow-sm border p-4 ${
                bank.is_active ? 'border-zinc-200' : 'border-zinc-100 opacity-60'
              }`}
            >
              <div className="flex items-center gap-4">
                {/* Logo */}
                <div className="w-20 h-16 bg-zinc-100 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                  {bank.logo_url ? (
                    <img 
                      src={bank.logo_url} 
                      alt={bank.name}
                      className="max-w-full max-h-full object-contain p-2"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <Building2 className="w-8 h-8 text-zinc-400" />
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="font-semibold text-zinc-900">{bank.name}</h3>
                    {bank.is_highlighted && (
                      <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full flex items-center gap-1">
                        <Star className="w-3 h-3" /> Destacado
                      </span>
                    )}
                    {bank.has_interest_free_option && (
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
                        Sin intereses
                      </span>
                    )}
                    {!bank.is_active && (
                      <span className="px-2 py-0.5 bg-zinc-100 text-zinc-500 text-xs font-medium rounded-full">
                        Inactivo
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-zinc-500">
                    {bank.max_months && <span>Hasta {bank.max_months} cuotas</span>}
                    {bank.min_rate && <span>Tasa desde {bank.min_rate}</span>}
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {bank.benefits && bank.benefits.map((benefit, idx) => (
                      <span key={idx} className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs rounded">
                        {benefit}
                      </span>
                    ))}
                  </div>
                  {bank.promo_expiration_date && (
                    <div className={`flex items-center gap-1 mt-2 text-xs ${
                      isExpired(bank.promo_expiration_date) ? 'text-red-500' : 'text-blue-600'
                    }`}>
                      <CalendarClock className="w-3 h-3" />
                      <span>
                        {isExpired(bank.promo_expiration_date) 
                          ? `Promo vencida: ${formatDate(bank.promo_expiration_date)}`
                          : `Promo hasta: ${formatDate(bank.promo_expiration_date)}`
                        }
                      </span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleHighlight(bank)}
                    className={`p-2 rounded-lg transition-colors ${
                      bank.is_highlighted 
                        ? 'bg-yellow-100 text-yellow-600' 
                        : 'bg-zinc-100 text-zinc-400 hover:text-yellow-600'
                    }`}
                    title={bank.is_highlighted ? 'Quitar destacado' : 'Destacar'}
                  >
                    <Star className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => toggleActive(bank)}
                    className={`p-2 rounded-lg transition-colors ${
                      bank.is_active 
                        ? 'bg-emerald-100 text-emerald-600' 
                        : 'bg-zinc-100 text-zinc-400'
                    }`}
                    title={bank.is_active ? 'Desactivar' : 'Activar'}
                  >
                    {bank.is_active ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={() => openEditModal(bank)}
                    className="p-2 bg-zinc-100 text-zinc-600 rounded-lg hover:bg-zinc-200 transition-colors"
                  >
                    <Pencil className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(bank.id)}
                    className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-zinc-200">
              <h2 className="text-xl font-bold text-zinc-900">
                {editingBank ? 'Editar Banco' : 'Nuevo Banco'}
              </h2>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">
                  Nombre del Banco *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">
                  Logo del Banco
                </label>
                <ImageUploader
                  currentImage={formData.logo_url}
                  onImageUploaded={(url) => setFormData({ ...formData, logo_url: url })}
                  folder="banks"
                />
                <p className="text-xs text-zinc-500 mt-1">
                  O pega una URL directa del logo
                </p>
                <input
                  type="url"
                  value={formData.logo_url}
                  onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                  placeholder="https://..."
                  className="w-full px-4 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 mt-2"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">
                    Cuotas Máximas
                  </label>
                  <input
                    type="number"
                    value={formData.max_months}
                    onChange={(e) => setFormData({ ...formData, max_months: e.target.value })}
                    placeholder="Ej: 48"
                    className="w-full px-4 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                  <p className="text-xs text-zinc-400 mt-1">Dejar vacío si no aplica</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">
                    Tasa Mínima
                  </label>
                  <input
                    type="text"
                    value={formData.min_rate}
                    onChange={(e) => setFormData({ ...formData, min_rate: e.target.value })}
                    placeholder="Ej: 12%"
                    className="w-full px-4 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                  <p className="text-xs text-zinc-400 mt-1">Dejar vacío si no aplica</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">
                  Fecha de Vencimiento de Promoción
                </label>
                <input
                  type="date"
                  value={formData.promo_expiration_date}
                  onChange={(e) => setFormData({ ...formData, promo_expiration_date: e.target.value })}
                  className="w-full px-4 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
                <p className="text-xs text-zinc-400 mt-1">Se mostrará en la página de financiamiento. Dejar vacío si no hay fecha límite.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">
                  Beneficios / Características
                </label>
                <div className="space-y-2">
                  {formData.benefits.map((benefit, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={benefit}
                        onChange={(e) => updateBenefit(index, e.target.value)}
                        placeholder="Ej: Aprobación rápida"
                        className="flex-1 px-4 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      />
                      {formData.benefits.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeBenefit(index)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addBenefit}
                    className="text-sm text-emerald-600 hover:text-emerald-700"
                  >
                    + Agregar beneficio
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">
                  Orden de aparición
                </label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>

              <div className="flex flex-col gap-3 p-4 bg-zinc-50 rounded-lg">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.has_interest_free_option}
                    onChange={(e) => setFormData({ ...formData, has_interest_free_option: e.target.checked })}
                    className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                  />
                  <span className="text-sm text-zinc-700">Tiene opción sin intereses</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_highlighted}
                    onChange={(e) => setFormData({ ...formData, is_highlighted: e.target.checked })}
                    className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                  />
                  <span className="text-sm text-zinc-700">Destacar banco (mostrará etiqueta "RECOMENDADO")</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                  />
                  <span className="text-sm text-zinc-700">Activo (visible en la página)</span>
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-zinc-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingBank(null);
                  }}
                  className="px-4 py-2 text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  {editingBank ? 'Guardar Cambios' : 'Crear Banco'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BanksPage;
